import { decodeBase64 } from "stream-chat";
import { AccountId, ChainId } from "caip";
import { xc20pDirEncrypter, xc20pDirDecrypter, createJWE, decryptJWE, } from "did-jwt";
import { prepareCleartext, decodeCleartext } from "dag-jose-utils";
import LitJsSdk from "lit-js-sdk";
import { ApiServiceImpl } from "../core/api.service.impl";
import { SiweMessage, SignatureType } from "lit-siwe";
import { base58btc } from "multiformats/bases/base58";
import { stores } from "../../stores/";
import { getChainFromAccountId } from "../../utils";
export class EncrypterServiceImpl extends ApiServiceImpl {
    constructor(accessControlService) {
        super();
        this.accessControlService = accessControlService;
        this.litClient = new LitJsSdk.LitNodeClient();
    }
    async authenticate() {
        await this.litClient.connect();
    }
    get accountId() {
        return stores.authStore.authSession?.accountId;
    }
    get authSig() {
        const siweMessage = stores.authStore.authSession
            ? siweFromCacao(stores.authStore.authSession.cacao)
            : undefined;
        return siweMessage
            ? {
                sig: siweMessage.signature,
                derivedVia: "web3.eth.personal.sign",
                signedMessage: siweMessage.prepareMessage(),
                address: siweMessage.address,
            }
            : undefined;
    }
    async encryptMessage(channel, profileTokenId, payload) {
        if (!stores.authStore.authSession) {
            return this.error(new Error("Encrypter not authenticated. Call authenticate() first"));
        }
        try {
            const { kid, symmetricKey } = await this.fetchOrCreateChannelKey(channel, profileTokenId);
            const encryptedMessage = await this.encrypt(payload, kid, symmetricKey);
            return this.success(encryptedMessage);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async decryptMessage(channel, message) {
        if (!stores.authStore.authSession) {
            return this.error(new Error("Encrypter not authenticated. Call authenticate() first"));
        }
        try {
            const { symmetricKey } = await this.fetchKeyFromKid(channel, "", message);
            const decryptedMsg = await this.decrypt(message, symmetricKey);
            return this.success(decryptedMsg);
        }
        catch (e) {
            return this.error(e);
        }
    }
    async encrypt(msg, kid, key) {
        const dirEncrypter = xc20pDirEncrypter(key);
        const cleartext = await prepareCleartext(msg);
        const jwe = await createJWE(cleartext, [dirEncrypter], { kid: kid });
        return jwe;
    }
    async decrypt(msg, key) {
        const dirDecrypter = xc20pDirDecrypter(key);
        const decryptedData = await decryptJWE(msg, dirDecrypter);
        return decodeCleartext(decryptedData);
    }
    /*
     *  Key management
     *
     *  Fetches a key for a given key ID (kid). The key is either already stored in local storage, or is fetched from Lit by providing the correct accessControlConditions.
     *  The kid can be obtained from a specific message payload. This is not the key itself, but is an identifier that can be used to index and store the key.
    `*/
    async fetchKeyFromKid(channel, profileTokenId, message) {
        const protectedHeader = JSON.parse(decodeBase64(message.protected));
        const decodedKey = base58btc.decode(protectedHeader.kid);
        const symmetricKey = await this.getOrFetchKeyForKid(channel, profileTokenId, protectedHeader.kid);
        return {
            kid: protectedHeader.kid,
            encryptedSymmetricKey: decodedKey,
            symmetricKey: symmetricKey,
        };
    }
    /*
     *  fetchOrCreateChannelKey
     *
     *  Fetches or creates the key needed for a given channel. Either reuses a key already stored for a channel or creates a new one.
    `*/
    async fetchOrCreateChannelKey(channel, profileTokenId) {
        if (!stores.authStore.authSession)
            throw new Error("Encrypter not authenticated. Call authenticate() first");
        let channelKey = localStorage.getItem(`hashchat:kids:${channel.id}`);
        await this.litClient.connect();
        const conditions = await this.accessControlService.generateAccessControlConditions(channel, profileTokenId);
        if (channelKey == null) {
            const { symmetricKey } = await LitJsSdk.encryptString("");
            const encryptedSymmetricKey = await this.litClient.saveEncryptionKey({
                ...conditions.data,
                symmetricKey,
                authSig: this.authSig,
                chain: getChainFromAccountId(stores.authStore.authSession.accountId),
            });
            const encodedKey = base58btc.encode(encryptedSymmetricKey).toString();
            localStorage.setItem(`hashchat:kids:${channel.id}`, encodedKey);
            this.saveKeyForKid(encodedKey, symmetricKey);
            return {
                kid: encodedKey,
                encryptedSymmetricKey: encryptedSymmetricKey,
                symmetricKey: symmetricKey,
            };
        }
        else {
            const symmetricKey = await this.getOrFetchKeyForKid(channel, profileTokenId, channelKey);
            const decodedKey = base58btc.decode(channelKey);
            return {
                kid: channelKey,
                encryptedSymmetricKey: decodedKey,
                symmetricKey: symmetricKey,
            };
        }
    }
    // TODO: Localstorage may not be the best solution due to security issues and XSS
    saveKeyForKid(kid, symmetricKey) {
        localStorage.setItem(`hashchat:keys:${kid}`, base58btc.encode(symmetricKey).toString());
    }
    /*
     *  Get or fetch key for Kid
     *
     *  Fetches a key for a given key ID (kid). The key is either already stored in local storage, or is fetched from Lit by providing the correct accessControlConditions.
    `*/
    async getOrFetchKeyForKid(channel, profileTokenId, kid) {
        if (!stores.authStore.authSession)
            throw new Error("Encrypter not authenticated. Call authenticate() first");
        const encodedKey = localStorage.getItem(`hashchat:keys:${kid}`);
        if (encodedKey) {
            return base58btc.decode(encodedKey);
        }
        else {
            const conditions = await this.accessControlService.generateAccessControlConditions(channel, profileTokenId);
            if (conditions.error)
                throw new Error(`Generating access control conditions failed: ${conditions.error}`);
            const decodedKid = base58btc.decode(kid);
            const symmetricKey = await this.litClient.getEncryptionKey({
                ...conditions.data,
                toDecrypt: LitJsSdk.uint8arrayToString(decodedKid, "base16"),
                chain: getChainFromAccountId(stores.authStore.authSession.accountId),
                authSig: this.authSig,
            });
            this.saveKeyForKid(kid, symmetricKey);
            return symmetricKey;
        }
    }
}
function siweFromCacao(cacao) {
    const account = AccountId.parse(cacao.p.iss.replace("did:pkh:", ""));
    const siwe = new SiweMessage({
        domain: cacao.p.domain,
        address: account.address,
        uri: cacao.p.aud,
        version: cacao.p.version,
        chainId: Number(new ChainId(account.chainId).reference),
    });
    if (cacao.p.statement)
        siwe.statement = cacao.p.statement;
    if (cacao.p.nonce)
        siwe.nonce = cacao.p.nonce;
    if (cacao.p.iat)
        siwe.issuedAt = cacao.p.iat;
    if (cacao.p.exp)
        siwe.expirationTime = cacao.p.exp;
    if (cacao.p.nbf)
        siwe.notBefore = cacao.p.nbf;
    if (cacao.p.requestId)
        siwe.requestId = cacao.p.requestId;
    if (cacao.p.resources)
        siwe.resources = cacao.p.resources;
    if (cacao.s) {
        if (cacao.s.s)
            siwe.signature = cacao.s.s;
        if (cacao.s.t === "eip191")
            siwe.type = SignatureType.PERSONAL_SIGNATURE;
    }
    return siwe;
}
